/**
 * Vera logomarks as inline SVG (so `currentColor` actually tints them — an
 * external SVG loaded via <img> would not inherit CSS color). Mirrors the
 * assets in `vera-design/assets/logo`.
 *
 *  - VeraMark      : filled rounded-seal mark (square fills with currentColor,
 *                    the check is paper). Use on light surfaces.
 *  - VeraMarkLine  : outline mark (everything is currentColor). Use on dark
 *                    surfaces — set color to paper/cream.
 *  - VeraLockup    : mark + "Vera" wordmark, all currentColor.
 *  - VeraSeal      : the institutional seal (currentColor), used as a faint
 *                    watermark.
 */

const PAPER = "#FAFAF7";

export function VeraMark({ size = 32, ...rest }: { size?: number } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Vera"
      {...rest}
    >
      <rect x="0.5" y="0.5" width="31" height="31" rx="6" fill="currentColor" />
      <rect x="10" y="8.5" width="12" height="1.6" rx="0.8" fill={PAPER} opacity="0.55" />
      <path d="M9.4 12 L16 22.4 L22.6 12" stroke={PAPER} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="16" cy="22.7" r="1.7" fill={PAPER} />
    </svg>
  );
}

export function VeraMarkLine({ size = 32, ...rest }: { size?: number } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Vera"
      {...rest}
    >
      <rect x="9" y="6.5" width="14" height="1.8" rx="0.9" fill="currentColor" opacity="0.55" />
      <path d="M8.4 11 L16 23 L23.6 11" stroke="currentColor" strokeWidth="3.1" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="16" cy="23.4" r="1.9" fill="currentColor" />
    </svg>
  );
}

export function VeraLockup({ height = 28, ...rest }: { height?: number } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      height={height}
      viewBox="0 0 170 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Vera"
      {...rest}
    >
      <rect x="0.5" y="8.5" width="31" height="31" rx="6" fill="currentColor" />
      <rect x="10" y="16.5" width="12" height="1.6" rx="0.8" fill={PAPER} opacity="0.55" />
      <path d="M9.4 20 L16 30.4 L22.6 20" stroke={PAPER} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="16" cy="30.7" r="1.7" fill={PAPER} />
      <text x="44" y="33" fontFamily="'Public Sans', sans-serif" fontSize="34" fontWeight="700" letterSpacing="-0.7" fill="currentColor">
        Vera
      </text>
    </svg>
  );
}

export function VeraSeal({ size = 120, ...rest }: { size?: number } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Vera seal"
      {...rest}
    >
      <defs>
        <path id="vera-arc-top" d="M 26 60 A 34 34 0 0 1 94 60" />
        <path id="vera-arc-bottom" d="M 30 60 A 30 30 0 0 0 90 60" />
      </defs>
      <circle cx="60" cy="60" r="54" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <circle cx="60" cy="60" r="46" stroke="currentColor" strokeWidth="2.4" fill="none" />
      <g stroke="currentColor" strokeWidth="1.4">
        <line x1="60" y1="6" x2="60" y2="14" />
        <line x1="60" y1="106" x2="60" y2="114" />
        <line x1="6" y1="60" x2="14" y2="60" />
        <line x1="106" y1="60" x2="114" y2="60" />
      </g>
      <text fill="currentColor" fontFamily="'Public Sans', sans-serif" fontSize="9.5" fontWeight="600" letterSpacing="3.4">
        <textPath href="#vera-arc-top" startOffset="50%" textAnchor="middle">
          VERIFICACIÓN
        </textPath>
      </text>
      <text fill="currentColor" fontFamily="'Public Sans', sans-serif" fontSize="8.5" fontWeight="600" letterSpacing="2.4">
        <textPath href="#vera-arc-bottom" startOffset="50%" textAnchor="middle">
          EXAMEN ÚNICO
        </textPath>
      </text>
      <g>
        <rect x="49" y="44" width="22" height="2.2" rx="1.1" fill="currentColor" opacity="0.5" />
        <path d="M48 50 L60 69 L72 50" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="60" cy="69.4" r="2.4" fill="currentColor" />
      </g>
    </svg>
  );
}
